import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { TagService } from './interface';
import { ACCOUNT, BOTH, MultiSelectTag, OPPORTUNITY } from './tag';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN, TAG_SERVICE_TOKEN } from './tokens';
import { AccountGroupApiService, AggregationSupportedFields } from '@vendasta/account-group';

@Injectable()
export class TagMultiSelectionStoreService {
  private readonly selectedAccountTags$$ = new ReplaySubject<MultiSelectTag[]>(1);
  readonly selectedAccountTags$ = this.selectedAccountTags$$.asObservable();
  private readonly selectedOppTags$$ = new ReplaySubject<MultiSelectTag[]>(1);
  readonly selectedOppTags$ = this.selectedOppTags$$.asObservable();

  readonly selectableOppTags$: Observable<MultiSelectTag[]>;
  readonly selectableAccTags$: Observable<MultiSelectTag[]>;

  constructor(
    private readonly accountGroupApi: AccountGroupApiService,
    @Inject(TAG_SERVICE_TOKEN) api: TagService,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
  ) {
    this.selectableOppTags$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) =>
        api.getTagsForOpportunities({
          partnerId: partnerId,
          marketId: marketId,
        }),
      ),
      map((tagResponse) => tagResponse.tags.map((t) => <MultiSelectTag>{ name: t, value: t, type: OPPORTUNITY })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.selectableAccTags$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) =>
        this.accountGroupApi.listOptionsForField({
          partnerId: partnerId,
          marketId: marketId,
          field: AggregationSupportedFields.AGGREGATION_TAGS,
        }),
      ),
      map((tags) => tags.values.map((t) => <MultiSelectTag>{ name: t, value: t, type: ACCOUNT })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public setTagsSelection(tags: MultiSelectTag[]): void {
    const accTags: MultiSelectTag[] = [];
    const oppTags: MultiSelectTag[] = [];

    tags.forEach((tag) => {
      if (tag.type === ACCOUNT || tag.type === BOTH) {
        accTags.push(tag);
      }
      if (tag.type === OPPORTUNITY || tag.type === BOTH) {
        oppTags.push(tag);
      }
    });

    this.selectedAccountTags$$.next(accTags);
    this.selectedOppTags$$.next(oppTags);
  }

  public setOppTagsSelection(tags: MultiSelectTag[]): void {
    this.selectedOppTags$$.next(tags || []);
  }

  public setAccTagsSelection(tags: MultiSelectTag[]): void {
    this.selectedAccountTags$$.next(tags || []);
  }

  public clearTagsSelection(): void {
    this.selectedAccountTags$$.next([]);
    this.selectedOppTags$$.next([]);
  }
}
